'use client';

import Link from 'next/link';
import { TypingText } from './TypingText';
import { HeroStones } from './HeroStones';
import { MagneticCta } from '../interaction/MagneticCta';
import { AvailabilityPill } from './AvailabilityPill';
import { CurrentlyBuilding } from './CurrentlyBuilding';
import { HeroTrustStrip } from './HeroTrustStrip';

type LatestWriting = {
  slug: string;
  title: string;
  display: string;
};

type Props = {
  latestWriting?: LatestWriting | null;
};

// Concrete, technical lines in the blog voice: the actual systems, not adjectives.
const PHRASES = [
  'Relational database written from scratch in Rust.',
  'Multi-tenant SaaS in daily use on a rental floor.',
  'RAG pipelines with evals that catch regressions.',
  'LLM features inside production applications.',
  'Postgres wire protocol, HNSW vector index, AVX2 kernels.',
];

export function HeroContent({ latestWriting }: Props = {}) {
  return (
    <div className="hero-over-canvas relative z-10 mx-auto max-w-content px-5 md:px-6 flex flex-col items-center text-center">
      {/* Calm scrim behind the interactive band (subtitle → CTAs → trust strip).
          Bright moon-caustic streaks + drifting koi cross this zone and, frame to
          frame, sometimes cut right behind the CTAs (owner visual audit). A soft
          elliptical darkening keeps the backdrop calm under the buttons regardless
          of animation phase, while fading out above (moon stays) and below (koi in
          the lower third stay). zIndex:-1 sits it behind the hero text but still
          above the page's pond canvas. Homepage-only — this component renders only
          the homepage hero. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2"
        style={{
          // Raised + taller so it spans the whole text column (byline → subtitle
          // → CTAs), not just the button band. Pairs with the PondSceneV3 camera
          // nudge (the scene-side half of the B+A hero-contrast fix): the camera
          // move drops the bright reflection band; this scrim adds human margin.
          top: '46%',
          transform: 'translate(-50%, -50%)',
          width: 'min(900px, 94vw)',
          height: 620,
          zIndex: -1,
          background:
            'radial-gradient(ellipse 54% 52% at 50% 50%, rgba(4,8,12,0.58) 0%, rgba(4,8,12,0.44) 42%, rgba(4,8,12,0) 78%)',
          filter: 'blur(6px)',
        }}
      />

      {/* Availability badge — moss heartbeat, gold reserved for CTA */}
      <AvailabilityPill />

      {/* Byline — name moved out of H1 */}
      <div
        data-hero-byline
        className="mt-5 font-mono"
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#ffffff',
          animation: 'fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both',
        }}
      >
        Alejandro Morales · AI Engineer
      </div>

      {/* Value-prop H1 — largest element on first paint */}
      <h1
        className="font-display italic text-text mt-3"
        style={{
          fontSize: 'clamp(40px, 6.5vw, 80px)',
          fontWeight: 400,
          lineHeight: 1.02,
          letterSpacing: '-0.035em',
          maxWidth: 920,
          animation: 'rise-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        I build production AI systems, from the storage engine to the model.
      </h1>

      {/* Rotating value-prop subline (typewriter retained — now stronger) */}
      <div
        className="mt-6 font-sans"
        style={{
          fontSize: 'clamp(20px, 2.2vw, 28px)',
          fontWeight: 500,
          lineHeight: 1.3,
          letterSpacing: 0,
          color: '#ffffff',
          height: 'calc(2 * 1.3em)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          animation: 'fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s both',
        }}
      >
        <TypingText phrases={PHRASES} startDelayMs={1400} holdMs={4200} typeSpeed={55} />
      </div>

      {/* Subtitle — concrete ICP + price anchor, single short sentence */}
      <p
        className="mt-4"
        style={{
          fontSize: 18,
          fontWeight: 400,
          lineHeight: 1.55,
          color: 'var(--hero-text-secondary, #f5edd9)',
          maxWidth: 580,
          animation: 'fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.9s both',
        }}
      >
        Open to full-time work as a Forward-Deployed Engineer or Applied AI Engineer. I embed with
        a team, learn how they actually work, and ship the system that replaces the manual process.
      </p>

      {/* Primary CTAs — single money-action gets the gradient */}
      <div
        className="mt-7 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-4 w-full sm:w-auto"
        style={{
          animation: 'fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1.1s both',
        }}
      >
        <MagneticCta radius={110} strength={10}>
          <Link
            href="/hire"
            className="cta-primary inline-flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
            style={{
              background: '#fbedd0',
              color: '#2a1f15',
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '0.02em',
              fontStyle: 'italic',
              padding: '15px 34px',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'inset 0 1px 0 rgba(255, 240, 220, 0.6), 0 6px 18px rgba(212, 165, 116, 0.20)',
            }}
          >
            Why hire me
            <span aria-hidden="true" className="ml-2" style={{ fontStyle: 'normal' }}>→</span>
          </Link>
        </MagneticCta>
        <Link
          href="/contact"
          data-secondary-cta
          className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-text-secondary"
          style={{
            color: 'var(--hero-text-muted, #c8b89a)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.04em',
            padding: '15px 8px',
            textDecoration: 'none',
          }}
        >
          <span>Get in touch</span>
          <span aria-hidden="true">→</span>
        </Link>
      </div>

      {/* Freelance stays available, but subordinated to the hiring CTAs above. */}
      <Link
        href="/services"
        className="mt-3 font-mono transition-colors duration-200 hover:text-text-secondary"
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: 'var(--hero-text-muted, #c8b89a)',
          textDecoration: 'none',
          animation: 'fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1.2s both',
        }}
      >
        Available for contract work →
      </Link>

      <HeroTrustStrip />

      {latestWriting && (
        <Link
          href={`/blog/${latestWriting.slug}`}
          className="mt-5 inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-100"
          style={{
            opacity: 0.8,
            animation: 'fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1.4s both',
          }}
        >
          <span
            className="font-mono"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--hero-text-muted, #c8b89a)',
            }}
          >
            Latest writing · {latestWriting.display}
          </span>
          <span
            className="font-sans"
            style={{ fontSize: 12, fontWeight: 500, color: 'var(--hero-text-secondary, #f5edd9)' }}
          >
            {latestWriting.title} →
          </span>
        </Link>
      )}

      <CurrentlyBuilding />
      <HeroStones />
    </div>
  );
}
